import { GatewayAPIClient, GatewayAPIError, gatewayAPIClient } from "../../app/api/client";

export interface ConversationMembership {
  user_id: string;
  role: string;
  joined_at: string;
}

export type ConversationLifecycleState = "active" | "closed" | "removed";

export interface ConversationLifecycleResponse {
  chat_id?: string;
  channel_id?: string;
  type: "group" | "channel";
  lifecycle_state: ConversationLifecycleState;
  owner_user_id: string;
  closed_at?: string;
  closed_by_user_id?: string;
  removed_at?: string;
  removed_by_user_id?: string;
}

export interface DeleteDirectConversationResponse {
  chat_id: string;
  type: "direct";
  deleted_for_user_id: string;
  deleted_at: string;
}

export interface ConversationSummary {
  chat_id: string;
  type: string;
  name?: string;
  description?: string;
  avatar_asset_id?: string;
  avatar_url?: string;
  participant_a_user_id?: string;
  participant_b_user_id?: string;
  participant_pair_key?: string;
  latest_sequence?: number;
  read_sequence?: number;
  unread_count?: number;
  owner_user_id?: string;
  lifecycle_state?: ConversationLifecycleState;
  closed_at?: string;
  closed_by_user_id?: string;
  removed_at?: string;
  removed_by_user_id?: string;
  created_at: string;
  updated_at?: string;
  memberships: ConversationMembership[];
}

export interface ConversationListResponse {
  conversations: ConversationSummary[];
  next_before_created_at?: string;
  next_before_conversation_id?: string;
}

export interface ListConversationsInput {
  limit?: number;
  beforeCreatedAt?: string;
  beforeConversationID?: string;
}

export interface ChatAttachment {
  media_id: string;
  kind: string;
}

export type MessageReferenceChatType = "direct" | "group" | "channel";

export interface MessageReferencePreview {
  chat_id?: string;
  chat_type?: MessageReferenceChatType;
  message_id: string;
  sequence: number;
  sender_user_id?: string;
  body_preview?: string;
  has_attachments?: boolean;
  created_at?: string;
}

export interface ChatMessage {
  message_id: string;
  chat_id: string;
  sequence: number;
  sender_user_id: string;
  body: string;
  ciphertext?: string;
  idempotency_key: string;
  ratchet_session_id?: string;
  ratchet_counter?: number;
  previous_chain_length?: number;
  edit_version: number;
  created_at: string;
  updated_at: string;
  edited_at?: string;
  deleted_at?: string;
  deleted_by_user_id?: string;
  root_post_id?: string;
  reply_to?: MessageReferencePreview;
  forwarded_from?: MessageReferencePreview;
  attachments?: ChatAttachment[];
}

export interface MessageListResponse {
  messages: ChatMessage[];
}

export interface ListMessagesInput {
  limit?: number;
  beforeSequence?: number;
}

export interface MessageContextResponse {
  chat_id: string;
  around_sequence: number;
  messages: ChatMessage[];
}

export interface ReceiptSummary {
  chat_id: string;
  user_id: string;
  delivered_seq: number;
  read_seq: number;
  updated_at: string;
}

export interface ReceiptParticipantSummary {
  user_id: string;
  delivered_seq: number;
  read_seq: number;
  updated_at?: string;
}

export interface ConversationReceiptSummary {
  chat_id: string;
  actor_user_id: string;
  participants: ReceiptParticipantSummary[];
}

export interface ListMessageContextInput {
  aroundSequence: number;
  before?: number;
  after?: number;
}

export interface SendChatMessagePayload {
  body?: string;
  idempotency_key?: string;
  attachments?: ChatAttachment[];
  reply_to_message_id?: string;
  forward_from_chat_id?: string;
  forward_from_message_id?: string;
}

export interface SendSecretMessagePayload {
  ciphertext: string;
  idempotency_key?: string;
  ratchet_session_id: string;
  ratchet_counter: number;
  previous_chain_length: number;
}

export interface InitiateMediaUploadInput {
  kind: string;
  filename?: string;
  declared_size_bytes: number;
}

export interface InitiateMediaUploadResponse {
  upload_id: string;
  kind: string;
  filename?: string;
  declared_size_bytes: number;
  max_size_bytes: number;
  binary_upload_url: string;
  binary_upload_method: "POST" | "PUT";
  created_at: string;
}

export interface UploadBinaryResponse {
  size_bytes: number;
  mime_type: string;
}

export interface FinalizeMediaUploadPayload {
  size_bytes: number;
  mime_type: string;
  filename?: string;
  metadata?: {
    width?: number;
    height?: number;
    duration_millis?: number;
    codec?: string;
  };
}

const VOICE_MAX_DURATION_MILLIS = 120_000;
const CONVERSATION_NAME_MAX_LENGTH = 64;
const CONVERSATION_DESCRIPTION_MAX_LENGTH = 280;
const MEDIA_ASSET_READY_POLL_INTERVAL_MS = 700;
const MEDIA_ASSET_READY_POLL_ATTEMPTS = 12;
const AVATAR_UPLOAD_CONTENT_TYPE_FALLBACK = "image/jpeg";
const AVATAR_UPLOAD_MIME_BY_EXTENSION = new Map<string, string>([
  ["jpg", "image/jpeg"],
  ["jpeg", "image/jpeg"],
  ["png", "image/png"],
  ["webp", "image/webp"],
  ["gif", "image/gif"],
  ["heic", "image/heic"],
  ["heif", "image/heif"],
]);
export const SAFE_FILE_UPLOAD_ACCEPT = ".pdf,.txt,.md,.csv,.json,.rtf,.doc,.xls,.ppt";
const FILE_UPLOAD_MAX_FILENAME_BYTES = 180;
const MESSAGE_REFERENCE_PREVIEW_FIELDS = new Set([
  "chat_id",
  "chat_type",
  "message_id",
  "sequence",
  "sender_user_id",
  "body_preview",
  "has_attachments",
  "created_at",
]);

export interface AvatarUploadContentTypeSource {
  type: string;
  name?: string;
}

export interface PhotoFinalizeMetadata {
  width: number;
  height: number;
}

export interface UploadAvatarMediaOptions {
  client?: ChatAPIClient;
  readMetadata?: (file: Blob) => Promise<PhotoFinalizeMetadata>;
  pollIntervalMillis?: number;
  pollAttempts?: number;
}

export interface FinalizeMediaUploadResponse {
  upload_id: string;
  asset_id: string;
  kind: string;
  filename?: string;
  status: string;
  size_bytes: number;
  mime_type: string;
  finalized_at: string;
}

export interface MediaAssetResponse {
  asset_id: string;
  kind: string;
  filename?: string;
  status: string;
  size_bytes: number;
  mime_type: string;
  metadata: {
    width?: number;
    height?: number;
    duration_millis?: number;
    codec?: string;
  };
  error_code?: string;
  uploaded_at: string;
  updated_at: string;
  ready_at?: string;
  failed_at?: string;
}

export interface MessageAttachmentMetadata {
  media_id: string;
  filename: string;
  kind: "file";
  status: string;
  size_bytes: number;
  mime_type: string;
}

interface CreateDirectConversationRequest {
  peer_user_id: string;
}

interface CreateDirectConversationResponse {
  chat_id: string;
}

interface CreateSecretConversationRequest {
  peer_user_id: string;
}

interface CreateSecretConversationResponse {
  chat_id: string;
}

interface CreateGroupConversationRequest {
  chat_id: string;
  name: string;
  description: string;
  avatar_asset_id?: string;
  member_user_ids: string[];
}

interface CreateGroupConversationResponse {
  chat_id: string;
}

interface CreateChannelConversationRequest {
  channel_id: string;
  name: string;
  description: string;
  avatar_asset_id?: string;
  publisher_user_ids: string[];
}

interface CreateChannelConversationResponse {
  channel_id: string;
}

interface ResolveUserResponse {
  user_id: string;
}

export interface GroupMembershipMutationResponse {
  chat_id: string;
  type: string;
  name?: string;
  description?: string;
  avatar_asset_id?: string;
  member_count: number;
  created_at: string;
}

export interface ChannelSubscriberMutationResponse {
  channel_id: string;
  type: string;
  name?: string;
  description?: string;
  avatar_asset_id?: string;
  member_count: number;
  created_at: string;
}

export interface UserProfileSummary {
  nickname?: string;
  display_name: string;
  username?: string;
  status_text: string;
  avatar_url: string;
}

export interface PushSubscriptionRecord {
  user_id: string;
  endpoint: string;
  public_key: string;
  auth_token: string;
  created_at: string;
  updated_at: string;
}

export interface ListPushSubscriptionsResponse {
  subscriptions: PushSubscriptionRecord[];
}

export interface RegisterPushSubscriptionPayload {
  endpoint: string;
  public_key: string;
  auth_token: string;
}

interface RevokePushSubscriptionResponse {
  revoked: boolean;
}

export interface CreateCollaborativeConversationInput {
  name: string;
  description?: string;
  avatarAssetID?: string;
  participantIdentifiers: string[];
}

export interface UpdateCollaborativeConversationMetadataInput {
  name: string;
  description?: string;
  avatarAssetID?: string | null;
}

type ChatAPIClient = Pick<GatewayAPIClient, "request">;

function getClient(client?: ChatAPIClient): ChatAPIClient {
  return client ?? gatewayAPIClient;
}

function buildPath(basePath: string, query: URLSearchParams): string {
  const encoded = query.toString();
  if (encoded === "") {
    return basePath;
  }
  return `${basePath}?${encoded}`;
}

function requireIdentifier(value: string, fieldName: string): string {
  const normalized = value.trim();
  if (normalized === "") {
    throw new Error(`${fieldName} must not be empty`);
  }
  if (normalized.includes("/")) {
    throw new Error(`${fieldName} contains invalid path separator`);
  }
  return normalized;
}

function requirePositiveSequence(sequence: number): number {
  if (!Number.isFinite(sequence) || Math.trunc(sequence) <= 0) {
    throw new Error("sequence must be > 0");
  }
  return Math.trunc(sequence);
}

function normalizeConversationName(value: string): string {
  const normalized = value.trim();
  if (normalized === "") {
    throw new Error("name must not be empty");
  }
  if (/\s/.test(normalized)) {
    throw new Error("name must be one word without spaces.");
  }
  if (normalized.length > CONVERSATION_NAME_MAX_LENGTH) {
    throw new Error(`name must be ${CONVERSATION_NAME_MAX_LENGTH} characters or fewer.`);
  }
  return normalized;
}

function normalizeConversationDescription(value: string | undefined): string {
  const normalized = value?.trim() ?? "";
  if (normalized.includes("\n") || normalized.includes("\r")) {
    throw new Error("description must be a single line");
  }
  if (normalized.length > CONVERSATION_DESCRIPTION_MAX_LENGTH) {
    throw new Error(`description must be ${CONVERSATION_DESCRIPTION_MAX_LENGTH} characters or fewer.`);
  }
  return normalized;
}

function normalizeOptionalAvatarAssetID(value: string | null | undefined): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  const normalized = value === null ? "" : value.trim();
  if (normalized.includes("/")) {
    throw new Error("avatar_asset_id contains invalid path separator");
  }
  return normalized;
}

function normalizeParticipantIdentifiers(identifiers: string[]): string[] {
  if (!Array.isArray(identifiers)) {
    throw new Error("participant identifiers must include at least one value");
  }

  const unique = new Set<string>();
  for (const identifier of identifiers) {
    const normalized = identifier.trim();
    if (normalized === "") {
      continue;
    }
    unique.add(normalized);
  }

  if (unique.size === 0) {
    throw new Error("participant identifiers must include at least one value");
  }

  return [...unique];
}

function slugifySegment(value: string): string {
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (slug === "") {
    return "chat";
  }
  return slug.slice(0, 24);
}

function createStableHash(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

function createGeneratedConversationID(prefix: "grp" | "chn", name: string, participantUserIDs: string[]): string {
  const normalizedParticipants = [...participantUserIDs].sort();
  const hash = createStableHash(`${name.toLowerCase()}|${normalizedParticipants.join("|")}`);
  const nameSegment = slugifySegment(name);
  return `${prefix}_${nameSegment}_${hash}`;
}

function looksLikeUserID(value: string): boolean {
  return value.startsWith("usr_");
}

async function resolveParticipantUserIDs(
  participantIdentifiers: string[],
  client?: ChatAPIClient,
): Promise<string[]> {
  const resolved: string[] = [];
  const seen = new Set<string>();

  for (const identifier of participantIdentifiers) {
    const resolvedUserID = looksLikeUserID(identifier)
      ? requireIdentifier(identifier, "participant user_id")
      : await resolveUserIDByIdentifier(identifier, client);
    if (!seen.has(resolvedUserID)) {
      resolved.push(resolvedUserID);
      seen.add(resolvedUserID);
    }
  }

  if (resolved.length === 0) {
    throw new Error("participant identifiers must include at least one value");
  }

  return resolved;
}

function randomIdempotencyKey(): string {
  const randomUUID = (globalThis.crypto as Crypto | undefined)?.randomUUID;
  if (typeof randomUUID === "function") {
    return randomUUID.call(globalThis.crypto);
  }
  return `idem-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function stringOrEmpty(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function requiredStringField(record: Record<string, unknown>, fieldName: string): string | null {
  const value = record[fieldName];
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim();
  return normalized === "" ? null : normalized;
}

function invalidGatewayResponse(details: unknown): never {
  throw new GatewayAPIError(500, "invalid_response", details);
}

const MEDIA_PRIVATE_RESPONSE_FIELDS = new Set<string>([
  ["object", "key"].join("_"),
  ["derivative", "object", "key"].join("_"),
  ["original", "object", "key"].join("_"),
  ["storage", "path"].join("_"),
  ["private", "path"].join("_"),
  ["upload", "token"].join("_"),
  ["storage", "url"].join("_"),
  ["buc", "ket"].join(""),
  "owner_user_id",
]);

function requireResponseObject(response: unknown): Record<string, unknown> {
  if (!response || typeof response !== "object" || Array.isArray(response)) {
    invalidGatewayResponse(response);
  }
  return response as Record<string, unknown>;
}

function rejectPrivateMediaResponseFields(record: Record<string, unknown>, details: unknown): void {
  for (const key of Object.keys(record)) {
    if (MEDIA_PRIVATE_RESPONSE_FIELDS.has(key)) {
      invalidGatewayResponse(details);
    }
  }
}

function requireResponseString(
  record: Record<string, unknown>,
  fieldName: string,
  details: unknown,
  options: { trim?: boolean; allowEmpty?: boolean } = {},
): string {
  const value = record[fieldName];
  if (typeof value !== "string") {
    invalidGatewayResponse(details);
  }
  const normalized = options.trim === false ? value : value.trim();
  if (options.allowEmpty !== true && normalized === "") {
    invalidGatewayResponse(details);
  }
  return normalized;
}

function optionalResponseString(
  record: Record<string, unknown>,
  fieldName: string,
  details: unknown,
): string | undefined {
  if (!Object.prototype.hasOwnProperty.call(record, fieldName)) {
    return undefined;
  }
  const value = record[fieldName];
  if (value === undefined) {
    return undefined;
  }
  if (typeof value !== "string") {
    invalidGatewayResponse(details);
  }
  const normalized = value.trim();
  return normalized === "" ? undefined : normalized;
}

function requireResponseNumber(record: Record<string, unknown>, fieldName: string, details: unknown): number {
  const value = record[fieldName];
  if (typeof value !== "number" || !Number.isFinite(value)) {
    invalidGatewayResponse(details);
  }
  return Math.trunc(value);
}

function optionalResponseNumber(
  record: Record<string, unknown>,
  fieldName: string,
  details: unknown,
): number | undefined {
  if (!Object.prototype.hasOwnProperty.call(record, fieldName)) {
    return undefined;
  }
  const value = record[fieldName];
  if (value === undefined) {
    return undefined;
  }
  if (typeof value !== "number" || !Number.isFinite(value)) {
    invalidGatewayResponse(details);
  }
  return Math.trunc(value);
}

function normalizeMessageReferenceChatType(
  value: unknown,
  details: unknown,
): MessageReferenceChatType | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (typeof value !== "string") {
    invalidGatewayResponse(details);
  }
  const normalized = value.trim();
  if (normalized === "") {
    return undefined;
  }
  if (normalized !== "direct" && normalized !== "group" && normalized !== "channel") {
    invalidGatewayResponse(details);
  }
  return normalized;
}

function normalizeMessageReferencePreview(
  value: unknown,
  fieldName: "reply_to" | "forwarded_from",
  details: unknown,
): MessageReferencePreview | undefined {
  if (!Object.prototype.hasOwnProperty.call(requireResponseObject(details), fieldName)) {
    return undefined;
  }
  if (value === undefined) {
    return undefined;
  }
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    invalidGatewayResponse(details);
  }

  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (!MESSAGE_REFERENCE_PREVIEW_FIELDS.has(key)) {
      invalidGatewayResponse(details);
    }
  }

  const messageID = requireResponseString(record, "message_id", details);
  const sequence = requireResponseNumber(record, "sequence", details);
  if (sequence <= 0) {
    invalidGatewayResponse(details);
  }

  const chatID = optionalResponseString(record, "chat_id", details);
  const chatType = normalizeMessageReferenceChatType(record.chat_type, details);
  const senderUserID = optionalResponseString(record, "sender_user_id", details);
  const bodyPreview = optionalResponseString(record, "body_preview", details);
  const createdAt = optionalResponseString(record, "created_at", details);
  const hasAttachmentsValue = record.has_attachments;
  if (
    Object.prototype.hasOwnProperty.call(record, "has_attachments") &&
    typeof hasAttachmentsValue !== "boolean"
  ) {
    invalidGatewayResponse(details);
  }

  return {
    ...(chatID === undefined ? {} : { chat_id: chatID }),
    ...(chatType === undefined ? {} : { chat_type: chatType }),
    message_id: messageID,
    sequence,
    ...(senderUserID === undefined ? {} : { sender_user_id: senderUserID }),
    ...(bodyPreview === undefined ? {} : { body_preview: bodyPreview }),
    ...(typeof hasAttachmentsValue === "boolean" ? { has_attachments: hasAttachmentsValue } : {}),
    ...(createdAt === undefined ? {} : { created_at: createdAt }),
  };
}

function normalizeResponseAttachment(value: unknown, details: unknown): ChatAttachment {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    invalidGatewayResponse(details);
  }
  const record = value as Record<string, unknown>;
  const mediaID = requireResponseString(record, "media_id", details);
  const kind = requireResponseString(record, "kind", details).toLowerCase();
  return {
    media_id: mediaID,
    kind,
  };
}

function normalizeResponseAttachments(record: Record<string, unknown>, details: unknown): ChatAttachment[] | undefined {
  if (!Object.prototype.hasOwnProperty.call(record, "attachments")) {
    return undefined;
  }
  const value = record.attachments;
  if (value === undefined) {
    return undefined;
  }
  if (!Array.isArray(value)) {
    invalidGatewayResponse(details);
  }
  const attachments = value.map((attachment) => normalizeResponseAttachment(attachment, details));
  return attachments.length === 0 ? undefined : attachments;
}

function normalizeChatMessageResponse(response: unknown): ChatMessage {
  const record = requireResponseObject(response);
  const messageID = requireResponseString(record, "message_id", response);
  const chatID = requireResponseString(record, "chat_id", response);
  const senderUserID = requireResponseString(record, "sender_user_id", response);
  const createdAt = requireResponseString(record, "created_at", response);
  const updatedAt = requireResponseString(record, "updated_at", response);
  const idempotencyKey = requireResponseString(record, "idempotency_key", response);
  const sequence = requireResponseNumber(record, "sequence", response);
  const editVersion = requireResponseNumber(record, "edit_version", response);
  const attachments = normalizeResponseAttachments(record, response);
  const replyTo = normalizeMessageReferencePreview(record.reply_to, "reply_to", response);
  const forwardedFrom = normalizeMessageReferencePreview(record.forwarded_from, "forwarded_from", response);

  return {
    message_id: messageID,
    chat_id: chatID,
    sequence,
    sender_user_id: senderUserID,
    body: requireResponseString(record, "body", response, { trim: false, allowEmpty: true }),
    ciphertext: optionalResponseString(record, "ciphertext", response),
    idempotency_key: idempotencyKey,
    ratchet_session_id: optionalResponseString(record, "ratchet_session_id", response),
    ratchet_counter: optionalResponseNumber(record, "ratchet_counter", response),
    previous_chain_length: optionalResponseNumber(record, "previous_chain_length", response),
    edit_version: editVersion,
    created_at: createdAt,
    updated_at: updatedAt,
    edited_at: optionalResponseString(record, "edited_at", response),
    deleted_at: optionalResponseString(record, "deleted_at", response),
    deleted_by_user_id: optionalResponseString(record, "deleted_by_user_id", response),
    root_post_id: optionalResponseString(record, "root_post_id", response),
    reply_to: replyTo,
    forwarded_from: forwardedFrom,
    attachments,
  };
}

function normalizeMessageListResponse(response: unknown): MessageListResponse {
  const record = requireResponseObject(response);
  if (!Array.isArray(record.messages)) {
    invalidGatewayResponse(response);
  }
  return {
    messages: record.messages.map((message) => normalizeChatMessageResponse(message)),
  };
}

function normalizeMessageContextResponse(response: unknown): MessageContextResponse {
  const record = requireResponseObject(response);
  if (!Array.isArray(record.messages)) {
    invalidGatewayResponse(response);
  }
  return {
    chat_id: requireResponseString(record, "chat_id", response),
    around_sequence: requireResponseNumber(record, "around_sequence", response),
    messages: record.messages.map((message) => normalizeChatMessageResponse(message)),
  };
}

function normalizeDeleteDirectConversationResponse(response: unknown): DeleteDirectConversationResponse {
  if (!response || typeof response !== "object" || Array.isArray(response)) {
    throw new GatewayAPIError(500, "invalid_response", response);
  }

  const record = response as Record<string, unknown>;
  const chatID = requiredStringField(record, "chat_id");
  const type = requiredStringField(record, "type");
  const deletedForUserID = requiredStringField(record, "deleted_for_user_id");
  const deletedAt = requiredStringField(record, "deleted_at");

  if (chatID === null || type !== "direct" || deletedForUserID === null || deletedAt === null) {
    throw new GatewayAPIError(500, "invalid_response", response);
  }

  return {
    chat_id: chatID,
    type: "direct",
    deleted_for_user_id: deletedForUserID,
    deleted_at: deletedAt,
  };
}

function normalizeAttachments(input: ChatAttachment[] | undefined): ChatAttachment[] | undefined {
  if (input === undefined || input.length === 0) {
    return undefined;
  }

  const normalized: ChatAttachment[] = [];
  for (const attachment of input) {
    const mediaID = requireIdentifier(attachment.media_id, "media_id");
    const kind = attachment.kind.trim().toLowerCase();
    if (kind === "") {
      throw new Error("attachment kind must not be empty");
    }
    normalized.push({
      media_id: mediaID,
      kind,
    });
  }

  return normalized;
}

export function normalizeFileUploadFilename(filename: string): string {
  const segments = filename.trim().split(/[\\/]+/).filter((segment) => segment.trim() !== "");
  const lastSegment = segments.length > 0 ? (segments[segments.length - 1] ?? "") : "";
  const basename = lastSegment.replace(/[\u0000-\u001f\u007f]+/g, " ").replace(/\s+/g, " ").trim();
  if (basename === "" || basename === "." || basename === "..") {
    throw new Error("filename must not be empty");
  }
  if (basename.includes("/") || basename.includes("\\")) {
    throw new Error("filename contains invalid path separator");
  }
  if (new TextEncoder().encode(basename).length > FILE_UPLOAD_MAX_FILENAME_BYTES) {
    throw new Error(`filename must be ${FILE_UPLOAD_MAX_FILENAME_BYTES} bytes or fewer`);
  }
  return basename;
}

function optionalFileUploadFilename(filename: string | undefined, kind: string): string | undefined {
  if (filename === undefined) {
    if (kind === "file") {
      throw new Error("filename must not be empty");
    }
    return undefined;
  }
  return normalizeFileUploadFilename(filename);
}

function normalizeOptionalPayloadIdentifier(value: string | undefined, fieldName: string): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  const normalized = requireIdentifier(value, fieldName);
  return normalized;
}

export function createChatMessageInput(payload: SendChatMessagePayload): SendChatMessagePayload {
  const body = payload.body?.trim() ?? "";
  const attachments = normalizeAttachments(payload.attachments);
  const idempotencyKey = payload.idempotency_key?.trim() ?? "";
  const replyToMessageID = normalizeOptionalPayloadIdentifier(payload.reply_to_message_id, "reply_to_message_id");
  const forwardFromChatID = normalizeOptionalPayloadIdentifier(payload.forward_from_chat_id, "forward_from_chat_id");
  const forwardFromMessageID = normalizeOptionalPayloadIdentifier(
    payload.forward_from_message_id,
    "forward_from_message_id",
  );
  if ((forwardFromChatID === undefined) !== (forwardFromMessageID === undefined)) {
    throw new Error("forward source must include chat_id and message_id");
  }
  if (body === "" && (attachments === undefined || attachments.length === 0) && forwardFromMessageID === undefined) {
    throw new Error("message body must not be empty");
  }

  return {
    body,
    idempotency_key: idempotencyKey === "" ? randomIdempotencyKey() : idempotencyKey,
    attachments,
    ...(replyToMessageID === undefined ? {} : { reply_to_message_id: replyToMessageID }),
    ...(forwardFromChatID === undefined ? {} : { forward_from_chat_id: forwardFromChatID }),
    ...(forwardFromMessageID === undefined ? {} : { forward_from_message_id: forwardFromMessageID }),
  };
}

function createSecretMessageInput(payload: SendSecretMessagePayload): {
  body: string;
  ciphertext: string;
  idempotency_key: string;
  ratchet_session_id: string;
  ratchet_counter: number;
  previous_chain_length: number;
} {
  const ciphertext = payload.ciphertext.trim();
  if (ciphertext === "") {
    throw new Error("ciphertext must not be empty");
  }

  const ratchetSessionID = requireIdentifier(payload.ratchet_session_id, "ratchet_session_id");
  if (!Number.isFinite(payload.ratchet_counter) || Math.trunc(payload.ratchet_counter) <= 0) {
    throw new Error("ratchet_counter must be > 0");
  }
  if (!Number.isFinite(payload.previous_chain_length) || Math.trunc(payload.previous_chain_length) < 0) {
    throw new Error("previous_chain_length must be >= 0");
  }

  const idempotencyKey = payload.idempotency_key?.trim() ?? "";

  return {
    body: "",
    ciphertext,
    idempotency_key: idempotencyKey === "" ? randomIdempotencyKey() : idempotencyKey,
    ratchet_session_id: ratchetSessionID,
    ratchet_counter: Math.trunc(payload.ratchet_counter),
    previous_chain_length: Math.trunc(payload.previous_chain_length),
  };
}

export async function listConversations(
  input: ListConversationsInput = {},
  client?: ChatAPIClient,
): Promise<ConversationListResponse> {
  const query = new URLSearchParams();
  if (typeof input.limit === "number" && Number.isFinite(input.limit) && input.limit > 0) {
    query.set("limit", String(Math.trunc(input.limit)));
  }
  if (input.beforeCreatedAt !== undefined && input.beforeCreatedAt.trim() !== "") {
    query.set("before_created_at", input.beforeCreatedAt.trim());
  }
  if (input.beforeConversationID !== undefined && input.beforeConversationID.trim() !== "") {
    query.set("before_conversation_id", input.beforeConversationID.trim());
  }

  return getClient(client).request<ConversationListResponse>({
    method: "GET",
    path: buildPath("/v1/chats", query),
  });
}

export async function createDirectConversation(peerUserID: string, client?: ChatAPIClient): Promise<string> {
  const payload: CreateDirectConversationRequest = {
    peer_user_id: requireIdentifier(peerUserID, "peer_user_id"),
  };

  const response = await getClient(client).request<CreateDirectConversationResponse>({
    method: "POST",
    path: "/v1/chats/direct",
    body: payload,
  });

  return requireIdentifier(response.chat_id, "chat_id");
}

export async function deleteDirectConversation(
  chatID: string,
  client?: ChatAPIClient,
): Promise<DeleteDirectConversationResponse> {
  const normalizedChatID = requireIdentifier(chatID, "chat_id");
  const response = await getClient(client).request<unknown>({
    method: "DELETE",
    path: `/v1/chats/direct/${encodeURIComponent(normalizedChatID)}`,
  });

  return normalizeDeleteDirectConversationResponse(response);
}

export async function createSecretConversation(peerUserID: string, client?: ChatAPIClient): Promise<string> {
  const payload: CreateSecretConversationRequest = {
    peer_user_id: requireIdentifier(peerUserID, "peer_user_id"),
  };

  const response = await getClient(client).request<CreateSecretConversationResponse>({
    method: "POST",
    path: "/v1/secret-chats",
    body: payload,
  });

  return requireIdentifier(response.chat_id, "chat_id");
}

export async function createGroupConversation(
  input: CreateCollaborativeConversationInput,
  client?: ChatAPIClient,
): Promise<string> {
  const normalizedName = normalizeConversationName(input.name);
  const normalizedDescription = normalizeConversationDescription(input.description);
  const participantIdentifiers = normalizeParticipantIdentifiers(input.participantIdentifiers);
  const memberUserIDs = await resolveParticipantUserIDs(participantIdentifiers, client);
  const generatedChatID = createGeneratedConversationID("grp", normalizedName, memberUserIDs);
  const normalizedAvatarAssetID = normalizeOptionalAvatarAssetID(input.avatarAssetID);

  const payload: CreateGroupConversationRequest = {
    chat_id: generatedChatID,
    name: normalizedName,
    description: normalizedDescription,
    member_user_ids: memberUserIDs,
  };
  if (normalizedAvatarAssetID !== undefined && normalizedAvatarAssetID !== "") {
    payload.avatar_asset_id = normalizedAvatarAssetID;
  }

  const response = await getClient(client).request<CreateGroupConversationResponse>({
    method: "POST",
    path: "/v1/chats/groups",
    body: payload,
  });

  return requireIdentifier(response.chat_id, "chat_id");
}

export async function createChannelConversation(
  input: CreateCollaborativeConversationInput,
  client?: ChatAPIClient,
): Promise<string> {
  const normalizedName = normalizeConversationName(input.name);
  const normalizedDescription = normalizeConversationDescription(input.description);
  const participantIdentifiers = normalizeParticipantIdentifiers(input.participantIdentifiers);
  const publisherUserIDs = await resolveParticipantUserIDs(participantIdentifiers, client);
  const generatedChannelID = createGeneratedConversationID("chn", normalizedName, publisherUserIDs);
  const normalizedAvatarAssetID = normalizeOptionalAvatarAssetID(input.avatarAssetID);

  const payload: CreateChannelConversationRequest = {
    channel_id: generatedChannelID,
    name: normalizedName,
    description: normalizedDescription,
    publisher_user_ids: publisherUserIDs,
  };
  if (normalizedAvatarAssetID !== undefined && normalizedAvatarAssetID !== "") {
    payload.avatar_asset_id = normalizedAvatarAssetID;
  }

  const response = await getClient(client).request<CreateChannelConversationResponse>({
    method: "POST",
    path: "/v1/channels",
    body: payload,
  });

  return requireIdentifier(response.channel_id, "channel_id");
}

export async function addGroupMember(
  groupID: string,
  userID: string,
  client?: ChatAPIClient,
): Promise<GroupMembershipMutationResponse> {
  const normalizedGroupID = requireIdentifier(groupID, "group_id");
  const normalizedUserID = requireIdentifier(userID, "user_id");

  return getClient(client).request<GroupMembershipMutationResponse>({
    method: "POST",
    path: `/v1/chats/groups/${encodeURIComponent(normalizedGroupID)}/members`,
    body: {
      user_id: normalizedUserID,
    },
  });
}

export async function addChannelSubscriber(
  channelID: string,
  userID: string,
  client?: ChatAPIClient,
): Promise<ChannelSubscriberMutationResponse> {
  const normalizedChannelID = requireIdentifier(channelID, "channel_id");
  const normalizedUserID = requireIdentifier(userID, "user_id");

  return getClient(client).request<ChannelSubscriberMutationResponse>({
    method: "POST",
    path: `/v1/channels/${encodeURIComponent(normalizedChannelID)}/subscribers`,
    body: {
      user_id: normalizedUserID,
    },
  });
}

export async function removeGroupMember(
  groupID: string,
  userID: string,
  client?: ChatAPIClient,
): Promise<GroupMembershipMutationResponse> {
  const normalizedGroupID = requireIdentifier(groupID, "group_id");
  const normalizedUserID = requireIdentifier(userID, "user_id");

  return getClient(client).request<GroupMembershipMutationResponse>({
    method: "DELETE",
    path: `/v1/chats/groups/${encodeURIComponent(normalizedGroupID)}/members/${encodeURIComponent(normalizedUserID)}`,
  });
}

export async function removeChannelSubscriber(
  channelID: string,
  userID: string,
  client?: ChatAPIClient,
): Promise<ChannelSubscriberMutationResponse> {
  const normalizedChannelID = requireIdentifier(channelID, "channel_id");
  const normalizedUserID = requireIdentifier(userID, "user_id");

  return getClient(client).request<ChannelSubscriberMutationResponse>({
    method: "DELETE",
    path: `/v1/channels/${encodeURIComponent(normalizedChannelID)}/subscribers/${encodeURIComponent(normalizedUserID)}`,
  });
}

export async function updateGroupConversationMetadata(
  groupID: string,
  input: UpdateCollaborativeConversationMetadataInput,
  client?: ChatAPIClient,
): Promise<GroupMembershipMutationResponse> {
  const normalizedGroupID = requireIdentifier(groupID, "group_id");
  const normalizedName = normalizeConversationName(input.name);
  const normalizedDescription = normalizeConversationDescription(input.description);
  const normalizedAvatarAssetID = normalizeOptionalAvatarAssetID(input.avatarAssetID);

  const body: {
    name: string;
    description: string;
    avatar_asset_id?: string;
  } = {
    name: normalizedName,
    description: normalizedDescription,
  };
  if (normalizedAvatarAssetID !== undefined) {
    body.avatar_asset_id = normalizedAvatarAssetID;
  }

  return getClient(client).request<GroupMembershipMutationResponse>({
    method: "PUT",
    path: `/v1/chats/groups/${encodeURIComponent(normalizedGroupID)}`,
    body,
  });
}

export async function updateChannelConversationMetadata(
  channelID: string,
  input: UpdateCollaborativeConversationMetadataInput,
  client?: ChatAPIClient,
): Promise<ChannelSubscriberMutationResponse> {
  const normalizedChannelID = requireIdentifier(channelID, "channel_id");
  const normalizedName = normalizeConversationName(input.name);
  const normalizedDescription = normalizeConversationDescription(input.description);
  const normalizedAvatarAssetID = normalizeOptionalAvatarAssetID(input.avatarAssetID);

  const body: {
    name: string;
    description: string;
    avatar_asset_id?: string;
  } = {
    name: normalizedName,
    description: normalizedDescription,
  };
  if (normalizedAvatarAssetID !== undefined) {
    body.avatar_asset_id = normalizedAvatarAssetID;
  }

  return getClient(client).request<ChannelSubscriberMutationResponse>({
    method: "PUT",
    path: `/v1/channels/${encodeURIComponent(normalizedChannelID)}`,
    body,
  });
}

export async function closeGroupConversation(
  groupID: string,
  client?: ChatAPIClient,
): Promise<ConversationLifecycleResponse> {
  const normalizedGroupID = requireIdentifier(groupID, "group_id");
  return getClient(client).request<ConversationLifecycleResponse>({
    method: "POST",
    path: `/v1/chats/groups/${encodeURIComponent(normalizedGroupID)}/close`,
  });
}

export async function removeGroupConversation(
  groupID: string,
  client?: ChatAPIClient,
): Promise<ConversationLifecycleResponse> {
  const normalizedGroupID = requireIdentifier(groupID, "group_id");
  return getClient(client).request<ConversationLifecycleResponse>({
    method: "DELETE",
    path: `/v1/chats/groups/${encodeURIComponent(normalizedGroupID)}`,
  });
}

export async function closeChannelConversation(
  channelID: string,
  client?: ChatAPIClient,
): Promise<ConversationLifecycleResponse> {
  const normalizedChannelID = requireIdentifier(channelID, "channel_id");
  return getClient(client).request<ConversationLifecycleResponse>({
    method: "POST",
    path: `/v1/channels/${encodeURIComponent(normalizedChannelID)}/close`,
  });
}

export async function removeChannelConversation(
  channelID: string,
  client?: ChatAPIClient,
): Promise<ConversationLifecycleResponse> {
  const normalizedChannelID = requireIdentifier(channelID, "channel_id");
  return getClient(client).request<ConversationLifecycleResponse>({
    method: "DELETE",
    path: `/v1/channels/${encodeURIComponent(normalizedChannelID)}`,
  });
}

export async function resolveUserIDByIdentifier(identifier: string, client?: ChatAPIClient): Promise<string> {
  const normalizedIdentifier = identifier.trim();
  if (normalizedIdentifier === "") {
    throw new Error("identifier must not be empty");
  }

  const response = await getClient(client).request<ResolveUserResponse>({
    method: "POST",
    path: "/v1/auth/resolve-user",
    body: {
      identifier: normalizedIdentifier,
    },
  });

  return requireIdentifier(response.user_id, "user_id");
}

export async function getProfileByUserID(userID: string, client?: ChatAPIClient): Promise<UserProfileSummary> {
  const normalizedUserID = requireIdentifier(userID, "user_id");

  const response = await getClient(client).request<Record<string, unknown>>({
    method: "GET",
    path: `/v1/profiles/${encodeURIComponent(normalizedUserID)}`,
  });

  return {
    nickname: stringOrEmpty(response.nickname),
    display_name: stringOrEmpty(response.display_name),
    username: stringOrEmpty(response.username),
    status_text: stringOrEmpty(response.status_text),
    avatar_url: stringOrEmpty(response.avatar_url),
  };
}

export async function listPushSubscriptions(
  userID: string,
  client?: ChatAPIClient,
): Promise<ListPushSubscriptionsResponse> {
  const normalizedUserID = requireIdentifier(userID, "user_id");
  return getClient(client).request<ListPushSubscriptionsResponse>({
    method: "GET",
    path: `/v1/users/${encodeURIComponent(normalizedUserID)}/push-subscriptions`,
  });
}

export async function registerPushSubscription(
  userID: string,
  payload: RegisterPushSubscriptionPayload,
  client?: ChatAPIClient,
): Promise<PushSubscriptionRecord> {
  const normalizedUserID = requireIdentifier(userID, "user_id");
  const endpoint = payload.endpoint.trim();
  const publicKey = payload.public_key.trim();
  const authToken = payload.auth_token.trim();
  if (endpoint === "" || publicKey === "" || authToken === "") {
    throw new Error("push subscription payload must include endpoint/public_key/auth_token");
  }

  return getClient(client).request<PushSubscriptionRecord>({
    method: "POST",
    path: `/v1/users/${encodeURIComponent(normalizedUserID)}/push-subscriptions`,
    body: {
      endpoint,
      public_key: publicKey,
      auth_token: authToken,
    },
  });
}

export async function revokePushSubscription(
  userID: string,
  endpoint: string,
  client?: ChatAPIClient,
): Promise<boolean> {
  const normalizedUserID = requireIdentifier(userID, "user_id");
  const normalizedEndpoint = endpoint.trim();
  if (normalizedEndpoint === "") {
    throw new Error("endpoint must not be empty");
  }

  const query = new URLSearchParams();
  query.set("endpoint", normalizedEndpoint);

  const response = await getClient(client).request<RevokePushSubscriptionResponse>({
    method: "DELETE",
    path: buildPath(`/v1/users/${encodeURIComponent(normalizedUserID)}/push-subscriptions`, query),
  });

  return response.revoked === true;
}

export async function listMessages(
  chatID: string,
  input: ListMessagesInput = {},
  client?: ChatAPIClient,
): Promise<MessageListResponse> {
  const normalizedChatID = requireIdentifier(chatID, "chat_id");

  const query = new URLSearchParams();
  if (typeof input.limit === "number" && Number.isFinite(input.limit) && input.limit > 0) {
    query.set("limit", String(Math.trunc(input.limit)));
  }
  if (
    typeof input.beforeSequence === "number" &&
    Number.isFinite(input.beforeSequence) &&
    input.beforeSequence >= 0
  ) {
    query.set("before_sequence", String(Math.trunc(input.beforeSequence)));
  }

  const response = await getClient(client).request<unknown>({
    method: "GET",
    path: buildPath(`/v1/chats/${encodeURIComponent(normalizedChatID)}/messages`, query),
  });
  return normalizeMessageListResponse(response);
}

export async function listMessageContext(
  chatID: string,
  input: ListMessageContextInput,
  client?: ChatAPIClient,
): Promise<MessageContextResponse> {
  const normalizedChatID = requireIdentifier(chatID, "chat_id");

  if (!Number.isFinite(input.aroundSequence) || Math.trunc(input.aroundSequence) <= 0) {
    throw new Error("around_sequence must be > 0");
  }
  const aroundSequence = Math.trunc(input.aroundSequence);

  const query = new URLSearchParams();
  query.set("around_sequence", String(aroundSequence));

  if (typeof input.before === "number" && Number.isFinite(input.before)) {
    const before = Math.trunc(input.before);
    if (before < 0) {
      throw new Error("before must be >= 0");
    }
    query.set("before", String(before));
  }

  if (typeof input.after === "number" && Number.isFinite(input.after)) {
    const after = Math.trunc(input.after);
    if (after < 0) {
      throw new Error("after must be >= 0");
    }
    query.set("after", String(after));
  }

  const response = await getClient(client).request<unknown>({
    method: "GET",
    path: buildPath(`/v1/chats/${encodeURIComponent(normalizedChatID)}/messages/context`, query),
  });
  return normalizeMessageContextResponse(response);
}

export async function getConversationReceiptSummary(
  chatID: string,
  client?: ChatAPIClient,
): Promise<ConversationReceiptSummary> {
  const normalizedChatID = requireIdentifier(chatID, "chat_id");

  return getClient(client).request<ConversationReceiptSummary>({
    method: "GET",
    path: `/v1/receipts/chats/${encodeURIComponent(normalizedChatID)}/summary`,
  });
}

export async function markConversationRead(
  chatID: string,
  sequence: number,
  client?: ChatAPIClient,
): Promise<ReceiptSummary> {
  const normalizedChatID = requireIdentifier(chatID, "chat_id");
  const normalizedSequence = requirePositiveSequence(sequence);

  return getClient(client).request<ReceiptSummary>({
    method: "POST",
    path: `/v1/receipts/chats/${encodeURIComponent(normalizedChatID)}/read`,
    body: {
      sequence: normalizedSequence,
    },
  });
}

export async function markConversationDelivered(
  chatID: string,
  sequence: number,
  client?: ChatAPIClient,
): Promise<ReceiptSummary> {
  const normalizedChatID = requireIdentifier(chatID, "chat_id");
  const normalizedSequence = requirePositiveSequence(sequence);

  return getClient(client).request<ReceiptSummary>({
    method: "POST",
    path: `/v1/receipts/chats/${encodeURIComponent(normalizedChatID)}/delivered`,
    body: {
      sequence: normalizedSequence,
    },
  });
}

export async function sendChatMessage(
  chatID: string,
  payload: SendChatMessagePayload,
  client?: ChatAPIClient,
): Promise<ChatMessage> {
  const normalizedChatID = requireIdentifier(chatID, "chat_id");
  const input = createChatMessageInput(payload);

  const response = await getClient(client).request<unknown>({
    method: "POST",
    path: `/v1/chats/${encodeURIComponent(normalizedChatID)}/messages`,
    body: input,
  });
  return normalizeChatMessageResponse(response);
}

export async function sendSecretMessage(
  chatID: string,
  payload: SendSecretMessagePayload,
  client?: ChatAPIClient,
): Promise<ChatMessage> {
  const normalizedChatID = requireIdentifier(chatID, "chat_id");
  const input = createSecretMessageInput(payload);

  const response = await getClient(client).request<unknown>({
    method: "POST",
    path: `/v1/chats/${encodeURIComponent(normalizedChatID)}/messages`,
    body: input,
  });
  return normalizeChatMessageResponse(response);
}

function normalizeBinaryUploadMethod(method: string): "POST" | "PUT" {
  const normalized = method.trim().toUpperCase();
  if (normalized === "POST" || normalized === "PUT") {
    return normalized;
  }
  throw new Error("binary upload method must be PUT or POST");
}

function normalizeInitiateMediaUploadResponse(response: unknown): InitiateMediaUploadResponse {
  const record = requireResponseObject(response);
  rejectPrivateMediaResponseFields(record, response);
  const declaredSizeBytes = requireResponseNumber(record, "declared_size_bytes", response);
  const maxSizeBytes = requireResponseNumber(record, "max_size_bytes", response);
  if (declaredSizeBytes <= 0 || maxSizeBytes <= 0) {
    invalidGatewayResponse(response);
  }
  return {
    upload_id: requireResponseString(record, "upload_id", response),
    kind: requireResponseString(record, "kind", response).toLowerCase(),
    filename: optionalResponseString(record, "filename", response),
    declared_size_bytes: declaredSizeBytes,
    max_size_bytes: maxSizeBytes,
    binary_upload_url: requireResponseString(record, "binary_upload_url", response),
    binary_upload_method: normalizeBinaryUploadMethod(requireResponseString(record, "binary_upload_method", response)),
    created_at: requireResponseString(record, "created_at", response),
  };
}

function normalizeUploadBinaryResponse(response: unknown): UploadBinaryResponse {
  const record = requireResponseObject(response);
  rejectPrivateMediaResponseFields(record, response);
  const sizeBytes = requireResponseNumber(record, "size_bytes", response);
  if (sizeBytes <= 0) {
    invalidGatewayResponse(response);
  }
  return {
    size_bytes: sizeBytes,
    mime_type: requireResponseString(record, "mime_type", response).toLowerCase(),
  };
}

function normalizeFinalizeMediaUploadResponse(response: unknown): FinalizeMediaUploadResponse {
  const record = requireResponseObject(response);
  rejectPrivateMediaResponseFields(record, response);
  const sizeBytes = requireResponseNumber(record, "size_bytes", response);
  if (sizeBytes <= 0) {
    invalidGatewayResponse(response);
  }
  return {
    upload_id: requireResponseString(record, "upload_id", response),
    asset_id: requireResponseString(record, "asset_id", response),
    kind: requireResponseString(record, "kind", response).toLowerCase(),
    filename: optionalResponseString(record, "filename", response),
    status: requireResponseString(record, "status", response).toLowerCase(),
    size_bytes: sizeBytes,
    mime_type: requireResponseString(record, "mime_type", response).toLowerCase(),
    finalized_at: requireResponseString(record, "finalized_at", response),
  };
}

function normalizeMediaAssetResponse(response: unknown): MediaAssetResponse {
  const record = requireResponseObject(response);
  rejectPrivateMediaResponseFields(record, response);
  const sizeBytes = requireResponseNumber(record, "size_bytes", response);
  if (sizeBytes <= 0) {
    invalidGatewayResponse(response);
  }
  const metadata = record.metadata;
  if (metadata === null || typeof metadata !== "object" || Array.isArray(metadata)) {
    invalidGatewayResponse(response);
  }
  return {
    asset_id: requireResponseString(record, "asset_id", response),
    kind: requireResponseString(record, "kind", response).toLowerCase(),
    filename: optionalResponseString(record, "filename", response),
    status: requireResponseString(record, "status", response).toLowerCase(),
    size_bytes: sizeBytes,
    mime_type: requireResponseString(record, "mime_type", response).toLowerCase(),
    metadata: metadata as MediaAssetResponse["metadata"],
    error_code: optionalResponseString(record, "error_code", response),
    uploaded_at: requireResponseString(record, "uploaded_at", response),
    updated_at: requireResponseString(record, "updated_at", response),
    ready_at: optionalResponseString(record, "ready_at", response),
    failed_at: optionalResponseString(record, "failed_at", response),
  };
}

export function resolveAvatarUploadContentType(file: AvatarUploadContentTypeSource): string {
  const explicitContentType = file.type.trim().toLowerCase().split(";")[0]?.trim() ?? "";
  if (explicitContentType.startsWith("image/")) {
    return explicitContentType === "image/jpg" ? "image/jpeg" : explicitContentType;
  }

  const filename = typeof file.name === "string" ? file.name.trim().toLowerCase() : "";
  const extensionStart = filename.lastIndexOf(".");
  const extension = extensionStart >= 0 ? filename.slice(extensionStart + 1) : "";
  const inferredContentType = AVATAR_UPLOAD_MIME_BY_EXTENSION.get(extension);
  if (inferredContentType !== undefined) {
    return inferredContentType;
  }

  if (explicitContentType !== "" && explicitContentType !== "application/octet-stream") {
    return explicitContentType;
  }

  return AVATAR_UPLOAD_CONTENT_TYPE_FALLBACK;
}

export async function readPhotoFinalizeMetadata(file: Blob): Promise<PhotoFinalizeMetadata> {
  if (
    typeof window === "undefined" ||
    typeof window.Image === "undefined" ||
    typeof URL === "undefined" ||
    typeof URL.createObjectURL !== "function"
  ) {
    throw new Error("Photo metadata is unavailable in this environment.");
  }

  const previewURL = URL.createObjectURL(file);
  try {
    return await new Promise<PhotoFinalizeMetadata>((resolve, reject) => {
      const image = new window.Image();
      image.onload = () => {
        const width = Math.trunc(image.naturalWidth);
        const height = Math.trunc(image.naturalHeight);
        if (width <= 0 || height <= 0) {
          reject(new Error("Photo dimensions are invalid."));
          return;
        }
        resolve({ width, height });
      };
      image.onerror = () => {
        reject(new Error("Failed to read photo metadata."));
      };
      image.src = previewURL;
    });
  } finally {
    URL.revokeObjectURL(previewURL);
  }
}

export async function initiateMediaUpload(
  input: InitiateMediaUploadInput,
  client?: ChatAPIClient,
): Promise<InitiateMediaUploadResponse> {
  const kind = input.kind.trim().toLowerCase();
  if (kind === "") {
    throw new Error("upload kind must not be empty");
  }
  if (!Number.isFinite(input.declared_size_bytes) || Math.trunc(input.declared_size_bytes) <= 0) {
    throw new Error("declared_size_bytes must be > 0");
  }
  const filename = optionalFileUploadFilename(input.filename, kind);

  const response = await getClient(client).request<unknown>({
    method: "POST",
    path: "/v1/uploads",
    body: {
      kind,
      ...(filename === undefined ? {} : { filename }),
      declared_size_bytes: Math.trunc(input.declared_size_bytes),
    },
  });

  return normalizeInitiateMediaUploadResponse(response);
}

export async function uploadMediaBinary(
  uploadPath: string,
  body: Blob,
  method: "POST" | "PUT" = "PUT",
  contentType?: string,
  client?: ChatAPIClient,
): Promise<UploadBinaryResponse> {
  const normalizedPath = uploadPath.trim();
  if (normalizedPath === "") {
    throw new Error("upload path must not be empty");
  }
  if (!(body instanceof Blob) || body.size <= 0) {
    throw new Error("upload body must be a non-empty blob");
  }

  const normalizedMethod = normalizeBinaryUploadMethod(method);
  const normalizedContentType = contentType?.trim();
  const headers = normalizedContentType ? { "Content-Type": normalizedContentType } : undefined;

  const response = await getClient(client).request<unknown>({
    method: normalizedMethod,
    path: normalizedPath,
    body,
    headers,
  });
  return normalizeUploadBinaryResponse(response);
}

export async function finalizeMediaUpload(
  uploadID: string,
  payload: FinalizeMediaUploadPayload,
  client?: ChatAPIClient,
): Promise<FinalizeMediaUploadResponse> {
  const normalizedUploadID = requireIdentifier(uploadID, "upload_id");
  const mimeType = payload.mime_type.trim().toLowerCase();
  if (mimeType === "") {
    throw new Error("mime_type must not be empty");
  }
  if (!Number.isFinite(payload.size_bytes) || Math.trunc(payload.size_bytes) <= 0) {
    throw new Error("size_bytes must be > 0");
  }
  const filename = payload.filename === undefined ? undefined : normalizeFileUploadFilename(payload.filename);

  const response = await getClient(client).request<unknown>({
    method: "POST",
    path: `/v1/uploads/${encodeURIComponent(normalizedUploadID)}/finalize`,
    body: {
      size_bytes: Math.trunc(payload.size_bytes),
      mime_type: mimeType,
      ...(filename === undefined ? {} : { filename }),
      metadata: payload.metadata ?? {},
    },
  });
  return normalizeFinalizeMediaUploadResponse(response);
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => {
    globalThis.setTimeout(resolve, milliseconds);
  });
}

export async function waitForReadyMediaAsset(
  assetID: string,
  initialStatus: string,
  options?: {
    client?: ChatAPIClient;
    pollIntervalMillis?: number;
    pollAttempts?: number;
  },
): Promise<string> {
  const normalizedAssetID = requireIdentifier(assetID, "asset_id");
  const normalizedInitialStatus = initialStatus.trim().toLowerCase();
  if (normalizedInitialStatus === "ready") {
    return normalizedAssetID;
  }
  if (normalizedInitialStatus === "failed") {
    throw new Error("Media processing failed.");
  }

  const pollAttempts = Math.max(1, Math.trunc(options?.pollAttempts ?? MEDIA_ASSET_READY_POLL_ATTEMPTS));
  const pollIntervalMillis = Math.max(
    0,
    Math.trunc(options?.pollIntervalMillis ?? MEDIA_ASSET_READY_POLL_INTERVAL_MS),
  );
  let lastKnownStatus = normalizedInitialStatus === "" ? "processing" : normalizedInitialStatus;

  for (let attempt = 0; attempt < pollAttempts; attempt += 1) {
    const asset = await getMediaAsset(normalizedAssetID, options?.client);
    const status = asset.status.trim().toLowerCase();
    if (status === "ready") {
      return asset.asset_id;
    }
    if (status === "failed") {
      const errorCode = asset.error_code?.trim() ?? "";
      if (errorCode !== "") {
        throw new Error(`Media processing failed (${errorCode}).`);
      }
      throw new Error("Media processing failed.");
    }

    lastKnownStatus = status === "" ? lastKnownStatus : status;
    if (attempt < pollAttempts - 1) {
      await delay(pollIntervalMillis);
    }
  }

  throw new Error(`Media processing timed out (${lastKnownStatus}).`);
}

export async function uploadAvatarMedia(file: File, options?: UploadAvatarMediaOptions): Promise<string> {
  if (!(file instanceof Blob) || file.size <= 0) {
    throw new Error("avatar upload file must be a non-empty image.");
  }

  const client = options?.client;
  const metadata = await (options?.readMetadata ?? readPhotoFinalizeMetadata)(file);
  const initiated = await initiateMediaUpload(
    {
      kind: "photo",
      declared_size_bytes: file.size,
    },
    client,
  );
  const uploaded = await uploadMediaBinary(
    initiated.binary_upload_url,
    file,
    initiated.binary_upload_method,
    resolveAvatarUploadContentType(file),
    client,
  );
  const finalized = await finalizeMediaUpload(
    initiated.upload_id,
    {
      size_bytes: uploaded.size_bytes,
      mime_type: uploaded.mime_type,
      metadata,
    },
    client,
  );

  const readyAssetID = await waitForReadyMediaAsset(finalized.asset_id, finalized.status, {
    client,
    pollIntervalMillis: options?.pollIntervalMillis,
    pollAttempts: options?.pollAttempts,
  });
  const normalizedAvatarAssetID = readyAssetID.trim();
  if (normalizedAvatarAssetID === "") {
    throw new Error("Avatar upload did not return an asset id.");
  }
  return normalizedAvatarAssetID;
}

export function createVoiceFinalizeMetadata(
  durationSeconds: number,
  codec = "opus",
): NonNullable<FinalizeMediaUploadPayload["metadata"]> {
  const normalizedCodec = codec.trim().toLowerCase() === "" ? "opus" : codec.trim().toLowerCase();
  const normalizedSeconds = Number.isFinite(durationSeconds) ? Math.max(1, Math.trunc(durationSeconds)) : 1;
  const durationMillis = Math.min(VOICE_MAX_DURATION_MILLIS, normalizedSeconds * 1000);

  return {
    duration_millis: durationMillis,
    codec: normalizedCodec,
  };
}

type MediaAssetReadPurpose = "message_attachment" | "conversation_avatar";

interface MediaAssetReadContext {
  conversationID: string;
  purpose: MediaAssetReadPurpose;
}

function buildMediaAssetPath(assetID: string, context?: MediaAssetReadContext): string {
  const normalizedAssetID = requireIdentifier(assetID, "asset_id");
  const basePath = `/v1/media/assets/${encodeURIComponent(normalizedAssetID)}`;
  const conversationID = context?.conversationID.trim() ?? "";
  if (context === undefined || conversationID === "") {
    return basePath;
  }
  const query = new URLSearchParams();
  query.set("conversation_id", conversationID);
  query.set("purpose", context.purpose);
  return buildPath(basePath, query);
}

function buildMediaAssetBinaryPath(assetID: string, context?: MediaAssetReadContext): string {
  const normalizedAssetID = requireIdentifier(assetID, "asset_id");
  const basePath = `/v1/media/assets/${encodeURIComponent(normalizedAssetID)}/binary`;
  const conversationID = context?.conversationID.trim() ?? "";
  if (context === undefined || conversationID === "") {
    return basePath;
  }
  const query = new URLSearchParams();
  query.set("conversation_id", conversationID);
  query.set("purpose", context.purpose);
  return buildPath(basePath, query);
}

function normalizeURLOrigin(origin: string): string {
  const trimmed = origin.trim();
  return trimmed.endsWith("/") ? trimmed.slice(0, -1) : trimmed;
}

export function resolveMessageAttachmentDownloadURL(
  mediaID: string,
  conversationID: string,
  gatewayOrigin?: string,
): string {
  const path = buildMediaAssetBinaryPath(mediaID, {
    conversationID,
    purpose: "message_attachment",
  });
  const origin = gatewayOrigin === undefined ? "" : normalizeURLOrigin(gatewayOrigin);
  return origin === "" ? path : `${origin}${path}`;
}

export async function getMediaAsset(
  assetID: string,
  client?: ChatAPIClient,
  context?: MediaAssetReadContext,
): Promise<MediaAssetResponse> {
  const normalizedAssetID = requireIdentifier(assetID, "asset_id");
  const response = await getClient(client).request<unknown>({
    method: "GET",
    path: buildMediaAssetPath(normalizedAssetID, context),
  });
  return normalizeMediaAssetResponse(response);
}

export async function getMessageAttachmentMetadata(
  mediaID: string,
  conversationID: string,
  client?: ChatAPIClient,
): Promise<MessageAttachmentMetadata> {
  const asset = await getMediaAsset(mediaID, client, {
    conversationID,
    purpose: "message_attachment",
  });
  if (asset.kind !== "file") {
    throw new GatewayAPIError(500, "invalid_response", asset);
  }
  const filename = asset.filename?.trim() || asset.asset_id;
  return {
    media_id: asset.asset_id,
    filename,
    kind: "file",
    status: asset.status,
    size_bytes: asset.size_bytes,
    mime_type: asset.mime_type,
  };
}

export async function editChatMessage(
  chatID: string,
  messageID: string,
  body: string,
  client?: ChatAPIClient,
): Promise<ChatMessage> {
  const normalizedChatID = requireIdentifier(chatID, "chat_id");
  const normalizedMessageID = requireIdentifier(messageID, "message_id");
  const trimmedBody = body.trim();
  if (trimmedBody === "") {
    throw new Error("message body must not be empty");
  }

  const response = await getClient(client).request<unknown>({
    method: "PATCH",
    path: `/v1/chats/${encodeURIComponent(normalizedChatID)}/messages/${encodeURIComponent(
      normalizedMessageID,
    )}`,
    body: { body: trimmedBody },
  });
  return normalizeChatMessageResponse(response);
}

export async function deleteChatMessage(
  chatID: string,
  messageID: string,
  client?: ChatAPIClient,
): Promise<ChatMessage> {
  const normalizedChatID = requireIdentifier(chatID, "chat_id");
  const normalizedMessageID = requireIdentifier(messageID, "message_id");

  const response = await getClient(client).request<unknown>({
    method: "DELETE",
    path: `/v1/chats/${encodeURIComponent(normalizedChatID)}/messages/${encodeURIComponent(
      normalizedMessageID,
    )}`,
  });
  return normalizeChatMessageResponse(response);
}

function decodeBase64URL(input: string): string | null {
  const normalized = input.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
  try {
    return atob(padded);
  } catch {
    return null;
  }
}

export function readActorUserIDFromAccessToken(accessToken: string | null): string | null {
  if (accessToken === null) {
    return null;
  }

  const parts = accessToken.split(".");
  if (parts.length < 2) {
    return null;
  }

  const payload = decodeBase64URL(parts[1] ?? "");
  if (payload === null) {
    return null;
  }

  try {
    const parsed = JSON.parse(payload) as Record<string, unknown>;
    const subject = parsed.sub;
    if (typeof subject !== "string") {
      return null;
    }
    const normalized = subject.trim();
    return normalized === "" ? null : normalized;
  } catch {
    return null;
  }
}
